import type { InputHTMLAttributes, ReactNode } from "react";

interface ReadoutProps {
  readonly label: string;
  readonly children: ReactNode;
}

/**
 * A labelled value with a hairline under it — a native input styled as a
 * readout, so the phone's own date and time pickers do the picking.
 */
export function Readout({ label, children }: ReadoutProps) {
  return (
    <label className="block border-b border-rule pb-3">
      <span className="label">{label}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}

type InputProps = InputHTMLAttributes<HTMLInputElement> & {
  readonly size?: never;
  readonly display?: boolean;
};

/** A bare input in the display face; the surrounding Readout supplies the rule. */
export function Input({ className = "", display = true, ...rest }: InputProps) {
  const face = display ? "font-display text-[26px] leading-tight" : "text-[17px]";
  return (
    <input
      className={`w-full appearance-none border-0 bg-transparent p-0 text-ink outline-none placeholder:text-faint ${face} ${className}`}
      {...rest}
    />
  );
}

interface BlockProps {
  readonly label?: string;
  readonly children: ReactNode;
}

/** A hairline-bounded section: structure from a rule, not a tinted box. */
export function Block({ label, children }: BlockProps) {
  return (
    <section className="border border-rule p-4">
      {label && <p className="label mb-3">{label}</p>}
      {children}
    </section>
  );
}

interface ChipProps {
  readonly label: string;
  readonly selected: boolean;
  readonly onSelect: () => void;
}

export function Chip({ label, selected, onSelect }: ChipProps) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      className={`min-h-11 border-[1.5px] px-4 text-[14px] ${
        selected ? "border-ink bg-ink text-paper" : "border-rule bg-paper text-ink"
      }`}
    >
      {label}
    </button>
  );
}

interface ErrorNoteProps {
  readonly message: string | null;
}

/** The reason something did not work, where the person is looking. */
export function ErrorNote({ message }: ErrorNoteProps) {
  if (!message) return null;
  return (
    <p role="alert" className="mt-3 text-[14px] leading-snug text-ink">
      {message}
    </p>
  );
}

type BoxInputProps = InputHTMLAttributes<HTMLInputElement> & {
  readonly label: string;
};

/**
 * A text field drawn as the same bordered box as the date and time pickers,
 * so every answer on first run looks like the same kind of thing.
 */
export function BoxInput({ label, id, className = "", ...rest }: BoxInputProps) {
  const inputId = id ?? rest.name;
  return (
    <div className="w-full">
      <label htmlFor={inputId} className="label mb-2 block">
        {label}
      </label>
      <div className="flex min-h-16 w-full items-center border-[1.5px] border-mid px-5 focus-within:border-ink">
        <input
          id={inputId}
          className={`w-full appearance-none border-0 bg-transparent p-0 font-display text-[22px] leading-tight text-ink outline-none placeholder:text-mid md:text-[24px] ${className}`}
          {...rest}
        />
      </div>
    </div>
  );
}
